/**
 * "Sync via extension" — opens the program's login URL in a new tab with a
 * #__rsvr=<requestId> fragment that arms the Reservoir browser extension to
 * scrape that specific tab. Polls /api/ext/sync/result for the outcome.
 *
 * Detection: the extension's bridge content script (manifest.json content_scripts
 * for the Reservoir origin) sets `window.__reservoirExt = { installed: true, ... }`
 * shortly after page load. We poll for that flag for ~1.5s before deciding the
 * extension isn't installed.
 *
 * Auth: every backend call is automatic-cookie-included (axios `withCredentials`).
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { ExternalLink, RefreshCw, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";

// Shared cache of which program codes the backend recognizes as
// extension-eligible. Refreshed at most once per page lifetime so per-row
// renders don't hammer the API. Sources from /api/net/programs/supported
// — the single registry now that DOM-scrape is gone.
let _eligibleCache = null;
async function fetchEligibleCodes() {
  if (_eligibleCache) return _eligibleCache;
  _eligibleCache = (async () => {
    try {
      const { data } = await api.get("/net/programs/supported");
      return new Set((data.programs || []).map((p) => p.code));
    } catch {
      return new Set();
    }
  })();
  return _eligibleCache;
}

export function useExtensionEligible(code) {
  const [ok, setOk] = useState(undefined);
  useEffect(() => {
    let cancelled = false;
    fetchEligibleCodes().then((set) => { if (!cancelled) setOk(set.has(code)); });
    return () => { cancelled = true; };
  }, [code]);
  return ok;
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS  = 5 * 60 * 1000; // 5 min — enough time for the user to log in + scrape

function useExtensionDetected() {
  const [installed, setInstalled] = useState(undefined); // undefined = still checking
  useEffect(() => {
    let attempts = 0;
    const t = setInterval(() => {
      if (typeof window !== "undefined" && window.__reservoirExt?.installed) {
        setInstalled(true);
        clearInterval(t);
      } else if (++attempts > 8) { // ~1.6s
        setInstalled(false);
        clearInterval(t);
      }
    }, 200);
    return () => clearInterval(t);
  }, []);
  return installed;
}

function statusCopy(job) {
  if (!job) return { line1: "Starting…", line2: "" };
  switch (job.status) {
    case "armed":      return { line1: "Sign in to your account in the new tab", line2: "We'll detect the balance automatically." };
    case "in_progress":return { line1: "Reading your balance…", line2: "" };
    case "completed":  return { line1: "Synced!", line2: "" };
    case "failed":     return { line1: "Sync failed", line2: job.error || "" };
    default:           return { line1: job.status, line2: "" };
  }
}

export default function SyncViaExtensionButton({ programCode, programLabel, onSynced, compact = false, label = "Sync via extension" }) {
  const installed = useExtensionDetected();
  const [busy, setBusy] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [job, setJob] = useState(null);
  const [err, setErr] = useState("");
  const pollRef = useRef(null);
  const deadlineRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  async function startSync() {
    setErr(""); setJob(null); setRequestId(null); setBusy(true);
    // Every program goes through the ReservoirAI XHR-intercept flow now
    // that the DOM-scrape path has been retired.
    try {
      const { data } = await api.post("/net/sync/arm", { program_code: programCode });
      const rid      = data.request_id;
      const loginUrl = data.login_url;
      setRequestId(rid);

      const sep = loginUrl.includes("#") ? "&" : "#";
      window.open(`${loginUrl}${sep}__rsvr_net=${rid}`, "_blank", "noopener");

      deadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
      pollRef.current = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/net/sync/result/${rid}`);
          setJob(j);
          if (j.status === "completed" || j.status === "failed") {
            clearInterval(pollRef.current);
            setBusy(false);
            if (j.status === "completed" && onSynced) onSynced(j);
          } else if (Date.now() > deadlineRef.current) {
            clearInterval(pollRef.current);
            setErr("Timed out waiting for the extension. Was it installed? Did you sign in?");
            setBusy(false);
          }
        } catch (e) {
          clearInterval(pollRef.current);
          setErr(e?.response?.data?.detail || e?.message || "polling failed");
          setBusy(false);
        }
      }, POLL_INTERVAL_MS);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "couldn't start sync");
      setBusy(false);
    }
  }

  // Not yet checked — render a neutral placeholder.
  if (installed === undefined && !busy) {
    return (
      <button
        type="button"
        disabled
        className={compact ? "inline-flex items-center gap-1.5 text-[11.5px] text-neutral-400 px-2 py-1" : "btn btn-ghost text-[12.5px] inline-flex items-center gap-1.5"}
      >
        <span className="spinner" /> Detecting…
      </button>
    );
  }

  if (installed === false) {
    return (
      <a
        href="/dashboard?install=ext"
        className={compact ? "inline-flex items-center gap-1.5 text-[11.5px] text-neutral-500 hover:text-black underline" : "btn btn-ghost text-[12.5px] inline-flex items-center gap-1.5 text-neutral-500"}
        title="The Reservoir browser extension isn't installed. Click for setup instructions."
        data-testid={`sync-ext-need-install-${programCode}`}
      >
        <ExternalLink size={12} /> Install extension
      </a>
    );
  }

  // Idle (installed, no active sync). In compact (table-row) mode, render
  // just the refresh icon. Non-compact mode keeps the labeled pill.
  if (!busy && !job) {
    if (compact) {
      return (
        <button
          type="button"
          onClick={startSync}
          title={`Sync ${programLabel || programCode} via extension`}
          className="btn btn-ghost text-[11.5px] py-1.5 px-2"
          data-testid={`sync-ext-${programCode}`}
        >
          <RefreshCw size={12} />
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={startSync}
        className="btn btn-ghost text-[12.5px] inline-flex items-center gap-1.5"
        data-testid={`sync-ext-${programCode}`}
        title={`Sync ${programLabel || programCode} by signing in to the carrier in a new tab`}
      >
        <Sparkles size={12} /> {label}
      </button>
    );
  }

  // Active or recently-completed sync — show inline status. We constrain
  // width with max-w + truncation so a long message can't overflow into
  // adjacent table columns (the bug seen on the Delta row's "Synced!"
  // floating past the Live Sync pill).
  const c = statusCopy(job);
  const isDone = job?.status === "completed";
  const isFail = job?.status === "failed" || !!err;
  const icon = isDone ? CheckCircle2 : isFail ? AlertCircle : RefreshCw;
  const IconEl = icon;
  return (
    <div
      className={(compact ? "inline-flex items-center gap-1.5 text-[11.5px] max-w-[140px]" : "flex items-center gap-2 text-[12.5px] max-w-[260px]")}
      data-testid={`sync-ext-status-${programCode}`}
    >
      <IconEl size={13} className={"shrink-0 " + (isDone ? "text-emerald-700" : isFail ? "text-red-600" : "text-neutral-500 animate-spin")} />
      <div className="min-w-0 flex-1">
        <div className={`truncate font-semibold ${isFail ? "text-red-700" : isDone ? "text-emerald-700" : "text-neutral-700"}`}>{err || c.line1}</div>
        {/* Hide the balance/tier sub-line in compact (table) mode — that data
            is already in the row's own balance + tier columns. Keep it in
            full mode (admin "Test sync" / standalone uses). */}
        {!compact && (c.line2 || (job?.result?.extracted?.balance)) && (
          <div className="text-[11px] text-neutral-500 truncate">
            {isDone && job?.result?.extracted?.balance
              ? `Balance: ${Number(job.result.extracted.balance).toLocaleString()} ${job.result.extracted.tier ? "· " + job.result.extracted.tier : ""}`
              : c.line2}
          </div>
        )}
      </div>
      {(isDone || isFail) && (
        <button onClick={() => { setJob(null); setRequestId(null); setErr(""); }} className="text-neutral-400 hover:text-black shrink-0" title="Dismiss">×</button>
      )}
    </div>
  );
}
